import { useDocumentTitle, useMembers, useOrgProfile } from '@/hooks';
import { qk } from '@/lib/queryKeys';
import {
  apiErrorName,
  changePassword,
  inviteMember,
  leaveOrganization,
  removeMember,
  resendInvitation,
  updateMemberRole,
  updateProfile,
} from '@/lib/recepthor-api';
import { Button, Input, Modal, Skel, useToast } from '@/ui';
import { EyeIcon, EyeOffIcon } from '@/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import ConfiguracionForm from './ConfiguracionForm';

import {
  InvitarMiembroModal,
  LeaveOrgModal,
  ManageMiembroModal,
} from '@/shared/OrgModales';

import { useAuth } from '@/app/AuthContext.jsx';
import {
  CAPABILITY_FULL_LABELS,
  MEMBERS_CAPABLE,
  orgArea,
} from '@/app/constants.js';
import { useCanAny, useCanAsMember, useCanReadOrgProfile } from '@/hooks';

import {
  mapOrgMembers,
  postLeaveDestination,
  visibleTemplatesFor,
} from '@/lib';
import { extractList } from '@/lib/apiList';
import EditarPerfilOrgModal from './EditarPerfilOrgModal.jsx';

// Compact "time since" for the password's last change (e.g. "45 d", "1 mes").
function formatAgo(value) {
  if (!value) return '—';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return '—';
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days < 1) return 'hoy';
  if (days < 30) return `${days} d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? 'mes' : 'meses'}`;
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? 'año' : 'años'}`;
}

// Leaving drops the org this screen is scoped to, so the members list is not
// refetched. refreshUser() re-resolves the active org: the user re-enters
// another one if any is left, and is logged out with the reason otherwise.
// Several query keys carry no organizationId, so the cache is dropped before
// landing, as selectOrganization does.
export async function leaveAndLand({
  leave,
  refreshUser,
  logout,
  navigate,
  queryClient,
}) {
  await leave();
  const result = await refreshUser();
  if (postLeaveDestination(result).kind === 'logout') {
    await logout();
    navigate('/login', { state: { leftLastOrg: true } });
    return;
  }
  queryClient.removeQueries();
  navigate('/');
}

// The resend toast and whether the members list must be re-read. A 409 or 404
// means the row is stale: the member accepted, or the invitation is gone.
export function resendFailure(err) {
  const name = apiErrorName(err?.body);
  if (name === 'MembershipAlreadyAccepted' || err?.status === 409) {
    return { message: 'Esta persona ya aceptó la invitación.', refresh: true };
  }
  if (name === 'MembershipNotFound' || err?.status === 404) {
    return { message: 'La invitación ya no existe.', refresh: true };
  }
  return {
    message: 'No se pudo reenviar la invitación. Intenta nuevamente.',
    refresh: false,
  };
}

export default function Configuracion() {
  useDocumentTitle('Configuración');
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumb, setPills, setHideOrgCrumb } = useOutletContext();
  const {
    user,
    organizationId,
    refreshUser,
    organizationName,
    activeCapability,
    isOwner,
    logout,
  } = useAuth();
  // Real org-admin permission strings the API emits (recepthor-api route
  // guards): org:manage-members → invite / change role / remove member;
  // org:update-profile → edit organization data. The Administrador role holds
  // both; non-admin roles hold neither.
  const canManageTeam = useCanAny(['org:manage-members', 'org:update-profile']);
  const canReadOrgProfile = useCanReadOrgProfile();
  // GET /memberships/members is guarded by org:manage-members, not by the
  // profile permission.
  const canListMembers = useCanAsMember('org:manage-members');
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [manageMember, setManageMember] = useState(null);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [section, setSection] = useState('');

  // Which /sistema area this render serves, derived from the path.
  const area = location.pathname.includes('/organizacion')
    ? 'organizacion'
    : 'cuenta';
  const isOrg = area === 'organizacion';
  // A membership that cannot read the org profile has nothing to see in the
  // organization area (/profile and /members would answer 403): it goes to
  // Cuenta. Not before the session resolves the active org, so a reload on
  // this page does not bounce someone who does have access.
  const orgAccessDenied = isOrg && organizationId != null && !canReadOrgProfile;
  // The "organización" area renames itself to the active org's kind
  // (Estudio / Receptor Judicial / Plataforma).
  const orgKind = orgArea(activeCapability);
  const areaLabel = isOrg ? orgKind.nav : 'Cuenta';
  const showMembers = MEMBERS_CAPABLE.has(activeCapability);

  // La sección activa vive en la URL (?tab=<key>): navegable con Atrás y
  // deep-linkeable (p. ej. /sistema/organizacion?tab=facturacion).
  const tabParam = new URLSearchParams(location.search).get('tab');
  const handleTabChange = useCallback(
    (key) => {
      const params = new URLSearchParams(location.search);
      params.set('tab', key);
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: false },
      );
    },
    [location.pathname, location.search, navigate],
  );

  // Facturación now lives on its own route (/facturacion, REC-1108).
  // Legacy jumps that pointed at the billing editor inside Configuración
  // (`?editar=facturacion` or `?tab=facturacion`, e.g. from the CheckoutModal
  // when the profile is missing) redirect to the facturación route, which owns
  // that data and its modals.
  const facturacionRedirect =
    isOrg &&
    !orgAccessDenied &&
    (new URLSearchParams(location.search).get('editar') === 'facturacion' ||
      new URLSearchParams(location.search).get('tab') === 'facturacion');
  useEffect(() => {
    if (facturacionRedirect) {
      navigate('/facturacion/tarifa', { replace: true });
    }
  }, [facturacionRedirect, navigate]);

  // Sistema breadcrumb omits the active-org head: "Sistema / <Área> / <Sección>".
  useEffect(() => {
    if (orgAccessDenied) return undefined;
    setHideOrgCrumb?.(true);
    setBreadcrumb([
      { label: 'Sistema', onClick: () => navigate('/sistema') },
      { label: areaLabel, onClick: () => navigate(`/sistema/${area}`) },
      ...(section ? [section] : []),
    ]);
    setPills([]);
    return () => {
      setHideOrgCrumb?.(false);
      setBreadcrumb([]);
      setPills([]);
    };
  }, [
    setBreadcrumb,
    setPills,
    setHideOrgCrumb,
    navigate,
    area,
    areaLabel,
    section,
    orgAccessDenied,
  ]);

  // Org DisplayProfile (Perfil section).
  // Un perfil que no se pudo leer rinde los mismos campos vacíos que un perfil
  // sin llenar, y sobre esa pantalla «Editar» abre el formulario con lo leído.
  // Guardar ahí escribe blancos sobre datos reales, así que la edición no se
  // ofrece cuando la lectura falló.
  const { data: orgProfileData, isError: perfilFailed } = useOrgProfile(
    organizationId,
    { enabled: isOrg && organizationId != null && canReadOrgProfile },
  );
  const perfil = useMemo(() => orgProfileData ?? {}, [orgProfileData]);

  // Org members (only for member-capable orgs; personal orgs skip the call).
  const membersQueryKey = qk.members(organizationId);
  const { data: membersData, isLoading: membersLoading } = useMembers(
    organizationId,
    {
      enabled:
        isOrg &&
        showMembers &&
        organizationId != null &&
        canReadOrgProfile &&
        canListMembers,
    },
  );
  const members = useMemo(
    () => (membersData ? mapOrgMembers(extractList(membersData)) : []),
    [membersData],
  );
  // Only the owner may promote a member to Administrador, so the selector
  // offers only what the acting user can choose.
  const templates = visibleTemplatesFor(membersData?.templates ?? [], isOwner);
  const refreshMembers = () =>
    queryClient.invalidateQueries({ queryKey: membersQueryKey });

  // The Cuenta area renders from the auth context alone — only the
  // Organización area waits on its org id before rendering.
  const isLoading = isOrg && organizationId == null;

  const cuenta = useMemo(
    () => ({
      nombre: user?.firstName ?? '—',
      apellido: user?.lastName ?? '',
      correo: user?.email ?? '—',
      passwordMask: '••••••••••••',
      passwordUpdatedLabel: formatAgo(
        user?.passwordUpdatedAt ?? user?.passwordChangedAt,
      ),
    }),
    [user],
  );

  const generalData = useMemo(() => {
    const owner = members.find((m) => m.rolVariant === 'owner');
    const userName = user
      ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
        user.email ||
        '—'
      : '—';
    return {
      nombre: organizationName || '—',
      tipo: CAPABILITY_FULL_LABELS[activeCapability] || activeCapability || '—',
      propietario: showMembers ? owner?.nombre || '—' : userName,
      miembrosActivos: showMembers
        ? members.filter((m) => m.estado === 'active').length
        : 1,
    };
  }, [organizationName, activeCapability, members, showMembers, user]);

  // The rejection propagates: the modal awaits this, closes only on success
  // and shows the failure otherwise, EmailDeliveryFailed included.
  async function handleInvitar(data) {
    // The API accepts an invite with no name, and the account asks for it
    // when it is set up.
    await inviteMember({ email: data.correo, templateId: data.templateId });
    refreshMembers();
  }
  // Both rejections propagate: the modal awaits them, closes only on success
  // and shows the hierarchy failure otherwise, OwnerOnlyAction included.
  async function handleSaveMember(updated) {
    if (!manageMember) return;
    await updateMemberRole(manageMember.id, { templateId: updated.templateId });
    refreshMembers();
  }
  async function handleRemoveMember() {
    if (!manageMember) return;
    await removeMember(manageMember.id);
    refreshMembers();
  }
  // A resend leaves the row INVITED, so a success refreshes nothing. A row
  // that went stale is re-read.
  async function handleReenviar(m) {
    try {
      await resendInvitation(m.id);
      toast({ kind: 'success', message: 'Invitación reenviada.' });
    } catch (err) {
      const { message, refresh } = resendFailure(err);
      toast({ kind: 'err', message });
      if (refresh) refreshMembers();
    }
  }
  // The rejection propagates: LeaveOrgModal awaits it and stays open on the
  // owner's OwnerOnlyAction refusal.
  function handleLeave() {
    return leaveAndLand({
      leave: leaveOrganization,
      refreshUser,
      logout,
      navigate,
      queryClient,
    });
  }

  function handleEditar(section) {
    if (section === 'password') {
      setEditing({ kind: 'password' });
      return;
    }
    if (section === 'personal') {
      setEditing({ kind: 'edit' });
      return;
    }
    if (section === 'perfil') {
      setEditing({ kind: 'perfil' });
      return;
    }
    // 'datos' (org general data edit) routes to the same EditarPerfilOrgModal
    // as 'perfil' — the stub is gone. Any other unrecognised key also routes
    // here rather than silently opening the Próximamente placeholder.
    if (section === 'datos') {
      setEditing({ kind: 'perfil' });
      return;
    }
    setEditing({ kind: section });
  }

  if (orgAccessDenied) {
    return <Navigate to="/sistema/cuenta" replace />;
  }

  if (isLoading) {
    return (
      <div
        style={{
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <Skel h={22} w={30} />
        <Skel w={90} />
        <Skel w={70} />
        <Skel w={50} />
        <Skel h={22} w={30} style={{ marginTop: '1rem' }} />
        <Skel w={90} />
        <Skel w={70} />
      </div>
    );
  }

  return (
    <>
      <ConfiguracionForm
        key={area}
        area={area}
        orgTitle={orgKind.title}
        cuenta={cuenta}
        generalData={generalData}
        perfil={perfil}
        perfilFailed={perfilFailed}
        miembros={members}
        membersLoading={membersLoading}
        showMembers={showMembers}
        canManageTeam={canManageTeam}
        actingIsOwner={isOwner}
        currentUserId={user?.id}
        onEditar={handleEditar}
        onVerMiembro={(m) => setManageMember(m)}
        onReenviar={handleReenviar}
        onInvitar={() => setShowInvite(true)}
        onLeave={() => setConfirmingLeave(true)}
        onSectionChange={setSection}
        activeTab={tabParam}
        onTabChange={handleTabChange}
      />
      <InvitarMiembroModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onSubmit={handleInvitar}
        templates={templates}
      />
      <ManageMiembroModal
        open={!!manageMember}
        onClose={() => setManageMember(null)}
        miembro={manageMember}
        templates={templates}
        onSave={handleSaveMember}
        onRemove={handleRemoveMember}
      />
      <LeaveOrgModal
        open={confirmingLeave}
        onClose={() => setConfirmingLeave(false)}
        onConfirm={handleLeave}
      />
      {editing && editing.kind === 'password' && (
        <CambiarContrasenaModal onClose={() => setEditing(null)} />
      )}
      {editing && editing.kind === 'edit' && (
        <EditarPerfilModal
          user={user}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await refreshUser();
            setEditing(null);
          }}
        />
      )}
      {editing && editing.kind === 'perfil' && (
        <EditarPerfilOrgModal
          perfil={perfil}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: qk.orgProfile.all(),
            });
            setEditing(null);
          }}
        />
      )}
      {editing &&
        editing.kind !== 'password' &&
        editing.kind !== 'edit' &&
        editing.kind !== 'perfil' && (
          <PendingFlowModal
            kind={editing.kind}
            onClose={() => setEditing(null)}
          />
        )}
    </>
  );
}

// Lightweight placeholder so Configuración buttons don't appear silently
// broken. Each kind gets a one-line description; replace with real edit
// modals as those workflows ship.
const PENDING_COPY = {
  // Bank accounts + payment methods moved to Facturación; their modals live in
  // the Facturación page now. This fallback covers any remaining unmapped kinds.
  pagar: 'El pago de boletas desde Configuración está en desarrollo.',
};

function PendingFlowModal({ kind, onClose }) {
  return (
    <Modal open onClose={onClose} size="small">
      <Modal.Header title="Próximamente" onClose={onClose} />
      <Modal.Body>
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          {PENDING_COPY[kind] ??
            'Esta acción aún no está disponible en este entorno.'}
        </p>
      </Modal.Body>
      <Modal.Foot>
        <Button variant="ghost" type="button" onClick={onClose}>
          Cerrar
        </Button>
      </Modal.Foot>
    </Modal>
  );
}

// ── EditarPerfilModal ────────────────────────────────────────────────────────

function PerfilField({ id, label, value, onChange, disabled = false, note }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label
        htmlFor={id}
        style={{
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: 'var(--ink-2)',
        }}
      >
        {label}
      </label>
      <Input
        id={id}
        value={value}
        onChange={onChange}
        disabled={disabled}
        readOnly={disabled}
        variant="mono"
      />
      {note && (
        <span
          style={{
            fontSize: '0.75rem',
            color: 'var(--ink-2)',
            opacity: 0.8,
          }}
        >
          {note}
        </span>
      )}
    </div>
  );
}

function EditarPerfilModal({ user, onClose, onSaved }) {
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e?.preventDefault();
    setError('');

    if (!firstName.trim()) {
      setError('El nombre es requerido.');
      return;
    }

    setLoading(true);
    try {
      await updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      await onSaved?.();
    } catch {
      setError('No se pudo guardar. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open onClose={onClose} size="small">
      <Modal.Header title="Editar información personal" onClose={onClose} />
      <Modal.Body>
        <form
          id="ep-form"
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          <PerfilField
            id="ep-first-name"
            label="Nombre"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <PerfilField
            id="ep-last-name"
            label="Apellido"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <PerfilField
            id="ep-email"
            label="Correo"
            value={user?.email ?? ''}
            disabled
            note="Para cambiar tu correo, contacta a soporte técnico."
          />
          {error && (
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--err)',
              }}
            >
              {error}
            </span>
          )}
          <button
            type="submit"
            aria-hidden="true"
            style={{ display: 'none' }}
          />
        </form>
      </Modal.Body>
      <Modal.Foot>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="seal"
          type="button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Guardando…' : 'Guardar'}
        </Button>
      </Modal.Foot>
    </Modal>
  );
}

// ── Password-field with eye toggle ───────────────────────────────────────────

function PasswordField({ id, label, value, onChange, error, show, onToggle }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label
        htmlFor={id}
        style={{
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: 'var(--ink-2)',
        }}
      >
        {label}
      </label>
      <div className="login-password-wrapper">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          error={!!error}
          autoComplete="new-password"
          required
          variant="mono"
        />
        <button
          type="button"
          className="login-password-toggle"
          onClick={onToggle}
          aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          tabIndex={-1}
        >
          {show ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {error && (
        <span
          style={{
            fontSize: '0.75rem',
            color: 'var(--err)',
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

// ── CambiarContrasenaModal ───────────────────────────────────────────────────

function CambiarContrasenaModal({ onClose }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [currentError, setCurrentError] = useState('');
  const [newError, setNewError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  function clearErrors() {
    setCurrentError('');
    setNewError('');
    setConfirmError('');
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    clearErrors();

    // Client-side validation
    if (newPassword !== confirmPassword) {
      setConfirmError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword === currentPassword) {
      setNewError('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setLoading(true);
    try {
      await changePassword({ currentPassword, newPassword, confirmPassword });
      setSuccess(true);
    } catch (err) {
      const apiMessage = err?.message ?? '';

      if (apiMessage.includes('Current password is incorrect')) {
        setCurrentError('La contraseña actual es incorrecta');
        return;
      }
      if (apiMessage.includes('different from current')) {
        setNewError('La nueva contraseña debe ser diferente a la actual');
        return;
      }
      if (apiMessage.includes('Passwords do not match')) {
        setConfirmError('Las contraseñas no coinciden');
        return;
      }
      if (
        err?.status === 400 &&
        /at least|uppercase|lowercase|number|special|complexity/i.test(
          apiMessage,
        )
      ) {
        setNewError(
          'Debe tener mínimo 8 caracteres, mayúscula, minúscula, número y carácter especial',
        );
        return;
      }
      if (err?.status === 401) {
        setCurrentError('La contraseña actual es incorrecta');
        return;
      }
      setCurrentError('No se pudo cambiar la contraseña. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <Modal open onClose={onClose} size="small">
        <Modal.Header title="Cambiar contraseña" onClose={onClose} />
        <Modal.Body>
          <p
            style={{
              margin: 0,
              lineHeight: 1.5,
              color: 'var(--ok)',
            }}
          >
            Contraseña actualizada correctamente.
          </p>
        </Modal.Body>
        <Modal.Foot>
          <Button variant="seal" type="button" onClick={onClose}>
            Cerrar
          </Button>
        </Modal.Foot>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} size="small">
      <Modal.Header title="Cambiar contraseña" onClose={onClose} />
      <Modal.Body>
        <form
          id="cp-form"
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          <PasswordField
            id="cp-current"
            label="Contraseña actual *"
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              if (currentError) setCurrentError('');
            }}
            error={currentError}
            show={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
          />
          <PasswordField
            id="cp-new"
            label="Nueva contraseña *"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (newError) setNewError('');
            }}
            error={newError}
            show={showNew}
            onToggle={() => setShowNew((v) => !v)}
          />
          <PasswordField
            id="cp-confirm"
            label="Confirmar nueva contraseña *"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (confirmError) setConfirmError('');
            }}
            error={confirmError}
            show={showConfirm}
            onToggle={() => setShowConfirm((v) => !v)}
          />
          <p
            style={{
              margin: 0,
              fontSize: '0.75rem',
              color: 'var(--ink-3)',
              lineHeight: 1.4,
            }}
          >
            Mínimo 8 caracteres, mayúscula, minúscula, número y carácter
            especial.
          </p>
          {/* Hidden submit so pressing Enter in any field submits the form */}
          <button
            type="submit"
            aria-hidden="true"
            style={{ display: 'none' }}
          />
        </form>
      </Modal.Body>
      <Modal.Foot>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="seal"
          type="button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Guardando…' : 'Cambiar contraseña'}
        </Button>
      </Modal.Foot>
    </Modal>
  );
}
